import { z } from "zod";
import { COST_CATEGORY_VALUES } from "./cost-category";
import { BASED_ON_VALUES, FIT_FOR_ORG_VALUES } from "./review-types";

const EvidenceRefSchema = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string().optional(),
  verified: z.boolean(),
  sourceType: z.enum(["library", "web_search", "unverified"]),
});

const EvidenceReferenceSchema = z.object({
  dedupKey: z.string(),
  citedText: z.string().nullable(),
  urls: z.array(z.string()).optional(),
  isInternal: z.boolean().optional(),
  value: z.number().finite().nullable().optional(),
  confidence: z.enum(["high", "moderate", "low"]).optional(),
});

const FactSourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["external", "catalog"]),
  url: z.string().url().nullable().optional(),
  excerpt: z.string().nullable().optional(),
  value: z.number().finite().nullable().optional(),
});

const EvidenceSnapshotSchema = z.object({
  combinedContext: z.string(),
  variables: z.array(
    z.object({
      variable: z.string(),
      source: z.enum(["library", "web_search"]),
      context: z.string(),
      citations: z.array(
        z.object({
          title: z.string(),
          url: z.string().optional(),
        }),
      ),
    }),
  ),
  retrievedAt: z.string(),
});

const ProposedMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  unit: z.string().optional(),
  direction: z.string().optional(),
  description: z.string().optional(),
  modelingStatus: z
    .enum(["modelable", "needs_baseline", "not_modelable"])
    .optional(),
  modelingIssue: z
    .object({
      requiredInput: z
        .object({
          key: z.string(),
          label: z.string(),
          unit: z.string(),
          grain: z.string().optional(),
        })
        .optional(),
      requiredConcept: z.string().nullable(),
      expectedUnit: z.string().nullable(),
      grain: z.string().nullable(),
      reason: z.string(),
      rejectedBaselineKeys: z.array(z.string()),
    })
    .optional(),
});

const ProposedValueStreamSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    valueStream: z.string(),
    direction: z.enum(["benefit", "cost"]),
    formulaDescription: z.string(),
    formulaExpr: z.string(),
    cadence: z.enum(["annual", "one-time", "one-time-with-rollout"]).optional(),
    segment: z.string().optional(),
    templateId: z.string().optional(),
    costTiming: z
      .enum(["front-loaded", "ongoing", "scales-with-rollout"])
      .optional(),
    costCategory: z.enum(COST_CATEGORY_VALUES).nullable().optional(),
    metricId: z.string().optional(),
    sources: z.array(EvidenceReferenceSchema).default([]),
    alerts: z.array(z.string()).optional(),
  })
  .superRefine((stream, ctx) => {
    if (
      stream.direction === "cost" &&
      stream.cadence === "one-time-with-rollout"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cadence"],
        message: "one-time-with-rollout cadence is only supported for benefits",
      });
    }
    if (
      stream.direction === "cost" &&
      stream.cadence === "one-time" &&
      stream.costTiming !== "front-loaded"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["costTiming"],
        message: "one-time cost cadence requires front-loaded cost timing",
      });
    }
  });

const ProposedAssumptionSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    default: z.number().finite().nullable(),
    range: z.object({
      low: z.number(),
      high: z.number(),
      step: z.number(),
    }),
    unit: z.string(),
    format: z.string(),
    linkedValueStream: z.string(),
    explanation: z.object({
      definition: z.string(),
      derivation: z.string(),
      sources: z.array(z.union([EvidenceRefSchema, z.string()])),
    }),
    basedOn: z.enum(BASED_ON_VALUES).optional(),
    fitForOrg: z.enum(FIT_FOR_ORG_VALUES).optional(),
    whatToConfirm: z.string().nullable().optional(),
    evidenceStatus: z
      .enum(["supported", "conflicting", "needs_input"])
      .optional(),
    sources: z.array(EvidenceReferenceSchema).default([]),
    alerts: z.array(z.string()).optional(),
  })
  .superRefine((assumption, ctx) => {
    const isInputRequired = assumption.evidenceStatus === "needs_input";
    if (assumption.default === null && !isInputRequired) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["default"],
        message: "default may be null only when evidenceStatus is needs_input",
      });
    }
  });

const RolloutPhaseSchema = z.object({
  startMonth: z.number().int().min(1),
  value: z.number().min(0),
  label: z.string().trim().min(1).optional(),
});

const RolloutPlanSchema = z
  .object({
    unit: z.enum(["percent", "providers"]),
    targetTotal: z.number().positive().optional(),
    phases: z.array(RolloutPhaseSchema).min(1),
    source: z.enum(["generated", "user"]),
  })
  .superRefine((plan, ctx) => {
    const seen = new Set<number>();
    let previous = 0;
    plan.phases.forEach((phase, index) => {
      if (seen.has(phase.startMonth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phases", index, "startMonth"],
          message: "startMonth must be unique",
        });
      }
      seen.add(phase.startMonth);
      if (phase.startMonth < previous) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phases", index, "startMonth"],
          message: "phases must be sorted by startMonth",
        });
      }
      previous = phase.startMonth;
      if (plan.unit === "percent" && phase.value > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phases", index, "value"],
          message: "percent value must be <= 100",
        });
      }
      if (
        plan.unit === "providers" &&
        plan.targetTotal != null &&
        phase.value > plan.targetTotal
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phases", index, "value"],
          message: "provider value must be <= targetTotal",
        });
      }
    });
    if (plan.unit === "providers" && plan.targetTotal == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetTotal"],
        message: "targetTotal is required for provider rollout plans",
      });
    }
  });

export const customModelDefinitionSchema = z.object({
  modelMode: z.enum(["ai_assisted", "manual_only"]),
  /** Intake critic verdict. Declared here as well as on the interface because
   *  `z.object` strips what it does not name: the adapter built this, the
   *  snapshot's `safeParse` succeeded, and every finding was dropped before
   *  the row was written. Optional — absent on pre-critic definitions. */
  validation: z
    .object({
      status: z.enum(["validated", "draft", "unvalidated"]),
      findings: z.array(
        z.object({
          severity: z.enum(["blocker", "warning"]),
          kind: z.string(),
          targetId: z.string(),
          message: z.string(),
          buyerResolvable: z.boolean(),
        }),
      ),
    })
    .optional(),
  valueStreams: z.array(ProposedValueStreamSchema),
  // Optional, NOT defaulted (see excludedValueStreamIds note below). Readers use
  // `definition.metrics ?? []`.
  metrics: z.array(ProposedMetricSchema).optional(),
  // Optional, NOT defaulted: a bare `.default([])` would inject the key on
  // every parse, changing the definition shape on version-snapshot round-trips
  // (and noop/diff detection). `getActiveValueStreams` treats absent as empty.
  excludedValueStreamIds: z.array(z.string()).optional(),
  assumptions: z.array(ProposedAssumptionSchema),
  contextKeys: z.array(z.string()).optional(),
  contextValues: z.record(z.string(), z.number()).optional(),
  contextOverrides: z.record(z.string(), z.number().finite()).optional(),
  contextMeta: z
    .record(
      z.string(),
      z.object({
        label: z.string(),
        description: z.string().optional(),
        unit: z.string().nullable(),
        sources: z.array(FactSourceSchema).optional(),
      }),
    )
    .optional(),
  formulaExpressions: z.record(z.string(), z.string()).optional(),
  modelGeneratedAt: z.string().optional(),
  modelGeneratedBy: z.string().optional(),
  userResponses: z.record(z.string(), z.number()).optional(),
  derivedFromCategoryId: z.string().optional(),
  derivedFromCategoryName: z.string().optional(),
  confirmedAssumptions: z
    .record(
      z.string(),
      z.object({
        confirmedAt: z.string(),
        confirmedValue: z.number(),
      }),
    )
    .optional(),
  annualBenefit: z.number().optional(),
  annualCost: z.number().optional(),
  implementationCost: z.number().optional(),
  confidenceRange: z
    .object({
      low: z.number(),
      high: z.number(),
    })
    .optional(),
  valueStreamAllocations: z.record(z.string(), z.number()).optional(),
  rationale: z.string().optional(),
  priorityAlignment: z
    .array(
      z.object({
        priorityId: z.string(),
        priorityName: z.string(),
        contributionShare: z.number(),
        reasoning: z.string(),
        kpiImpacts: z.array(
          z.object({
            kpiId: z.string(),
            kpiName: z.string(),
            estimatedImpact: z.number(),
            impactReasoning: z.string(),
          }),
        ),
      }),
    )
    .optional(),
  evidenceSnapshot: EvidenceSnapshotSchema.optional(),
  rampProfile: z
    .object({
      implementation_months: z.number(),
      ramp_months: z.number(),
      ramp_start_pct: z.number(),
    })
    .optional(),
  rolloutPlan: RolloutPlanSchema.optional(),
});

/**
 * Backwards-compatible alias for the renamed export.
 * Prefer {@link customModelDefinitionSchema} for new code.
 */
export const CustomModelDefinitionSchema = customModelDefinitionSchema;

/**
 * Looser variant for INBOUND API payloads (e.g. PUT /opportunities/:id/roi-model).
 *
 * Stored computable models are gated by `assertComputableModelDefinition` at
 * every write boundary. The schema itself permits empty arrays so a terminal
 * metric modeling outcome can be persisted without fabricated ROI inputs.
 * Non-browser
 * clients (e2e, future manual_only wizard steps) can legitimately POST a
 * partial body; rejecting them at the Zod layer with a generic 400 obscures
 * the real "must include valueStreams and an assumptions array" message that
 * the domain validator raises afterwards. So the inbound schema allows the
 * arrays to be missing/empty and lets `assertComputableModelDefinition`
 * produce the user-readable error. Note an empty `assumptions` array clears
 * that validator: a value stream fed only by context facts has no slider
 * assumptions, and rejecting it stranded such models unsaveable.
 */
export const customModelDefinitionInputSchema = customModelDefinitionSchema
  .omit({ valueStreams: true, assumptions: true })
  .extend({
    valueStreams: z.array(ProposedValueStreamSchema).optional(),
    assumptions: z.array(ProposedAssumptionSchema).optional(),
  });
